
import React, { useState } from 'react';
import BookOpenIcon from './icons/BookOpenIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';

interface GuideSectionProps {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const GuideSection: React.FC<GuideSectionProps> = ({ title, children, isOpen, onToggle }) => {
  return (
    <div className="border-b border-steel-blue last:border-b-0">
      <button
        onClick={onToggle}
        className="flex justify-between items-center w-full py-4 text-left"
        aria-expanded={isOpen}
      >
        <h3 className="text-lg font-semibold text-off-white">{title}</h3>
        <ChevronDownIcon className={`w-6 h-6 text-light-blue transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`grid grid-rows-[0fr] transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr]' : ''}`}>
        <div className="overflow-hidden">
            <div className="pb-4 text-light-blue space-y-2">
            {children}
            </div>
        </div>
      </div>
    </div>
  );
};


const BitcoinGuide: React.FC = () => {
    const [openSection, setOpenSection] = useState<string | null>('What is Bitcoin?');

    const handleToggle = (title: string) => {
        setOpenSection(openSection === title ? null : title);
    };

    const sections = [
        { 
            title: 'What is Bitcoin?', 
            content: (
                <p>Bitcoin is a decentralized digital currency, created in 2009 by an unknown person or group of people using the name Satoshi Nakamoto. It operates on a technology called a blockchain, which is a distributed public ledger that records all transactions. Unlike traditional currencies issued by governments (like the U.S. Dollar), Bitcoin is not controlled by any single entity, making it resistant to censorship and inflation.</p>
            )
        },
        { 
            title: 'What is Market Capitalization?', 
            content: (
                <p>Market Capitalization (or 'Market Cap') is a key metric used to gauge the size and value of a cryptocurrency. It's calculated by multiplying the current market price of a single coin by the total number of coins in circulation. For Bitcoin, this would be: <code className="bg-dark-blue p-1 rounded-md text-sm">Current BTC Price × Circulating Supply</code>. A higher market cap generally indicates a more established and stable asset, though it is still subject to market volatility.</p>
            )
        },
        {
            title: 'Understanding the Fear & Greed Index',
            content: (
                <>
                    <p>The Crypto Fear & Greed Index provides a daily sentiment analysis of the cryptocurrency market. It gathers data from various sources, including volatility, market momentum, social media trends, and surveys. The index is scored from 0 to 100:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>0-24 (Extreme Fear):</strong> Indicates investors are overly worried, which could be a buying opportunity.</li>
                        <li><strong>25-49 (Fear):</strong> General concern in the market.</li>
                        <li><strong>50 (Neutral):</strong> The market is not leaning strongly in either direction.</li>
                        <li><strong>51-74 (Greed):</strong> Suggests investors are becoming overly bullish, which could mean the market is due for a correction.</li>
                        <li><strong>75-100 (Extreme Greed):</strong> A sign of extreme bullishness and potential market euphoria, often a signal to be cautious.</li>
                    </ul>
                </>
            )
        },
        {
            title: 'A Note on Capital Gains Tax',
            content: (
                <p>When you sell or trade cryptocurrency for a profit, the earnings are typically subject to capital gains tax. The tax rate can vary significantly based on your location, income, and how long you held the asset (short-term vs. long-term). The tax feature in this calculator provides a simplified estimate and should not be considered tax advice. We strongly recommend consulting with a qualified tax professional to understand your specific obligations.</p>
            )
        },
        {
            title: 'How to Secure Your Bitcoin',
            content: (
                <>
                    <p>Protecting your Bitcoin is crucial. Unlike a bank account, transactions are irreversible. Here are two primary methods for storing your crypto:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Hot Wallets:</strong> These are software wallets connected to the internet (e.g., mobile or desktop apps). They are convenient for frequent transactions but are more vulnerable to online threats.</li>
                        <li><strong>Cold Wallets (Hardware Wallets):</strong> These are physical devices that store your keys offline, providing the highest level of security against hacking. Examples include Ledger and Trezor. For long-term holding, a hardware wallet is the recommended standard.</li>
                    </ul>
                </>
            )
        }
    ];

  return (
    <div className="bg-navy-blue p-6 rounded-lg shadow-xl mb-8">
      <div className="flex items-center mb-4">
        <BookOpenIcon className="w-8 h-8 mr-3 text-btc-orange" />
        <h2 className="text-2xl font-bold text-off-white">Bitcoin Knowledge Base</h2>
      </div>
      <div className="space-y-2">
        {sections.map(section => (
            <GuideSection 
                key={section.title}
                title={section.title}
                isOpen={openSection === section.title}
                onToggle={() => handleToggle(section.title)}
            >
                {section.content}
            </GuideSection>
        ))}
      </div>
    </div>
  );
};

export default BitcoinGuide;
